import * as THREE from 'three'
import type { SelectableHint } from '@/composables/useMeshHighlighting'
import type { useViewerSelection } from '@/composables/useViewerSelection'
import type { useInstancePicking } from '@/composables/useInstancePicking'

type ViewerSelection = ReturnType<typeof useViewerSelection>
type InstancePicking = ReturnType<typeof useInstancePicking>

export type ViewerInteractionDeps = {
  selection: Pick<
    ViewerSelection,
    | 'hasSelection'
    | 'selectionMeshes'
    | 'selectMesh'
    | 'selectPart'
    | 'isHoverable'
    | 'hoverGroup'
    | 'handleKey'
    | 'clearSelection'
  >
  instancePicking: Pick<
    InstancePicking,
    'active' | 'toggle' | 'isPickable' | 'hoverGroup' | 'render' | 'stop'
  >
  highlights: {
    hoverChanged: (mesh: THREE.Mesh | null) => boolean
    applyHover: (meshes: THREE.Mesh[]) => void
    clearHover: () => void
    applySelectionHighlight: (meshes: THREE.Mesh[]) => void
  }
  requestRender: () => void
}

export function useViewerInteraction(deps: ViewerInteractionDeps) {
  const { selection, instancePicking, highlights } = deps

  function selectMesh(mesh: THREE.Mesh | null) {
    if (instancePicking.active.value) instancePicking.toggle(mesh)
    else selection.selectMesh(mesh)
  }

  function selectPart(hint: SelectableHint) {
    instancePicking.stop()
    selection.selectPart(hint)
  }

  function hoverMesh(mesh: THREE.Mesh | null) {
    if (!highlights.hoverChanged(mesh)) return
    const picking = instancePicking.active.value
    if (!mesh || !(picking ? instancePicking.isPickable(mesh) : selection.isHoverable(mesh))) {
      highlights.clearHover()
    } else {
      highlights.applyHover(picking ? instancePicking.hoverGroup(mesh) : selection.hoverGroup(mesh))
    }
    deps.requestRender()
  }

  function handleKey(e: KeyboardEvent): boolean {
    if (instancePicking.active.value && e.key === 'Escape') {
      instancePicking.stop()
      return true
    }
    return selection.handleKey(e)
  }

  function restoreOverlay() {
    if (selection.hasSelection.value) {
      highlights.applySelectionHighlight(selection.selectionMeshes())
    } else if (instancePicking.active.value) {
      instancePicking.render()
    }
  }

  function clear() {
    instancePicking.stop()
    selection.clearSelection()
  }

  return { selectMesh, selectPart, hoverMesh, handleKey, restoreOverlay, clear }
}
